import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../../../components/base/Button';
import ShareModal from '../../../../components/base/ShareModal';
import ShareToMessageModal, { SharedContent } from '../../../../components/base/ShareToMessageModal';
import { useAuth } from '../../../../hooks/useAuth';
import { messagesApi } from '../../../../lib/api';
import { createGroupUrl } from '../../../../hooks/useNavigation';

interface GroupHeaderProps {
  group: {
    id: string;
    sequential_id?: number;
    name: string;
    description: string;
    cover_image: string;
    category: string;
    privacy: string;
    member_count: number;
    owner_id: string;
  };
  isMember: boolean;
  userRole: string | null;
  currentUserId: string | null;
  onJoinLeave: () => void;
  onOpenSettings: () => void;
  onOpenInvite?: () => void;
  isLoading?: boolean;
}

const GroupHeader = ({ group, isMember, userRole, currentUserId, onJoinLeave, onOpenSettings, onOpenInvite, isLoading = false }: GroupHeaderProps) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isJoining, setIsJoining] = useState(false);
  const [isHoveringJoinBtn, setIsHoveringJoinBtn] = useState(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [isShareToMessageOpen, setIsShareToMessageOpen] = useState(false);
  const [shareError, setShareError] = useState<string | null>(null);

  const handleJoinLeave = async () => {
    if (!currentUserId) {
      navigate('/');
      return;
    }
    setIsJoining(true);
    await onJoinLeave();
    setIsJoining(false);
  };

  const getCategoryColor = (category: string) => {
    const colors: Record<string, string> = {
      'Entertainment': 'bg-purple-100 text-purple-700',
      'Production': 'bg-blue-100 text-blue-700',
      'Genre': 'bg-pink-100 text-pink-700',
      'Regional': 'bg-green-100 text-green-700',
      'Professional': 'bg-orange-100 text-orange-700',
    };
    return colors[category] || 'bg-gray-100 text-gray-700';
  };

  const isOwnerOrAdmin = userRole === 'owner' || userRole === 'admin';

  const shareUrl = `${window.location.origin}${createGroupUrl(group.sequential_id || 0, group.name)}`;

  const sharedContent: SharedContent = {
    type: 'post',
    id: group.id,
    title: group.name,
    description: group.description,
    image: group.cover_image,
    url: shareUrl,
    meta: {
      category: group.category,
      categoryColor: getCategoryColor(group.category).includes('purple') ? '#7c3aed' 
        : getCategoryColor(group.category).includes('blue') ? '#2563eb'
        : getCategoryColor(group.category).includes('pink') ? '#db2777'
        : getCategoryColor(group.category).includes('green') ? '#16a34a'
        : getCategoryColor(group.category).includes('orange') ? '#ea580c'
        : '#6b7280',
    },
  };

  const handleShareToMessage = async (conversationId: string, content: SharedContent) => {
    if (!user) return;

    try {
      setShareError(null);
      const messageContent = `🔗 Check out this group: ${content.title}\n${content.url}`;
      
      const response = await messagesApi.sendMessage(conversationId, messageContent);
      
      if (!response.success) {
        throw new Error(response.error || 'Failed to send message');
      }
    } catch (error: any) {
      console.error('Error sharing to message:', error);
      setShareError(error.message || 'Failed to share. Please try again.');
      throw error;
    }
  };

  const handleStartNewConversation = async (userId: string, content: SharedContent) => {
    if (!user) return;

    try {
      setShareError(null);
      
      // Create new conversation
      const conversationResponse = await messagesApi.createConversation([userId]);
      
      if (!conversationResponse.success || !conversationResponse.data) {
        throw new Error(conversationResponse.error || 'Failed to create conversation');
      }

      // Send the message
      await handleShareToMessage(conversationResponse.data.id, content);
    } catch (error: any) {
      console.error('Error starting conversation:', error);
      setShareError(error.message || 'Failed to start conversation. Please try again.');
      throw error;
    }
  };

  return (
    <>
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <div className="relative h-64">
          <img
            src={group.cover_image}
            alt={group.name}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"></div>
          <div className="absolute bottom-0 left-0 right-0 p-6">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${getCategoryColor(group.category)}`}>
                    {group.category}
                  </span>
                  {group.privacy === 'private' && (
                    <span className="flex items-center gap-1 text-white/90 text-sm">
                      <i className="ri-lock-line"></i>
                      Private
                    </span>
                  )}
                </div>
                <h1 className="text-3xl font-bold text-white mb-2">{group.name}</h1>
                <p className="text-white/90 text-base max-w-3xl">{group.description}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-gray-100">
          {shareError && (
            <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">
              <i className="ri-error-warning-line mr-2"></i>
              {shareError}
            </div>
          )}
          
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <i className="ri-group-line text-gray-500 text-lg"></i>
                <span className="text-gray-700 font-medium">{group.member_count.toLocaleString()}</span>
                <span className="text-gray-500 text-sm">members</span>
              </div>
            </div>

            <div className="flex items-center gap-3">
              {isMember && (
                <button
                  onClick={() => onOpenInvite ? onOpenInvite() : null}
                  className="flex items-center gap-2 px-4 py-2 text-teal-600 hover:text-teal-700 hover:bg-teal-50 rounded-lg transition-colors cursor-pointer"
                >
                  <i className="ri-user-add-line"></i>
                  <span className="text-sm font-medium whitespace-nowrap">Invite Members</span>
                </button>
              )}
              <button
                onClick={() => setIsShareModalOpen(true)}
                className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
              >
                <i className="ri-share-line"></i>
                <span className="text-sm font-medium">Share</span>
              </button>
              {isOwnerOrAdmin && (
                <Button
                  variant="outline"
                  onClick={onOpenSettings}
                  className="flex items-center gap-2"
                  disabled={isLoading}
                >
                  <i className="ri-settings-3-line"></i>
                  Settings
                </Button>
              )}
              {currentUserId && (
                <button
                  onClick={handleJoinLeave}
                  onMouseEnter={() => setIsHoveringJoinBtn(true)}
                  onMouseLeave={() => setIsHoveringJoinBtn(false)}
                  disabled={isJoining || isLoading}
                  className={`inline-flex items-center justify-center gap-2 font-medium rounded-lg transition-all px-5 py-2 text-sm min-w-[140px] whitespace-nowrap cursor-pointer ${
                    isJoining || isLoading
                      ? 'bg-gray-100 text-gray-400 border border-gray-200 cursor-not-allowed'
                      : isMember
                        ? isHoveringJoinBtn
                          ? 'bg-red-50 text-red-600 border border-red-300 hover:bg-red-100'
                          : 'bg-gray-100 text-gray-500 border border-gray-200'
                        : 'bg-teal-500 text-white hover:bg-teal-600 border border-teal-500'
                  }`}
                >
                  {isJoining || isLoading ? (
                    <>
                      <i className="ri-loader-4-line animate-spin"></i>
                      {isMember ? 'Leaving...' : 'Joining...'}
                    </>
                  ) : isMember ? (
                    isHoveringJoinBtn ? (
                      <>
                        <i className="ri-logout-box-line"></i>
                        Leave Group
                      </>
                    ) : (
                      <>
                        <i className="ri-check-line"></i>
                        Joined
                      </>
                    )
                  ) : (
                    <>
                      <i className="ri-user-add-line"></i>
                      Join Group
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <ShareModal
        isOpen={isShareModalOpen}
        onClose={() => setIsShareModalOpen(false)}
        title={group.name}
        url={shareUrl}
        onShareToMessage={() => {
          setIsShareModalOpen(false);
          setIsShareToMessageOpen(true);
        }}
      />

      <ShareToMessageModal
        isOpen={isShareToMessageOpen}
        onClose={() => setIsShareToMessageOpen(false)}
        content={sharedContent}
        onSend={handleShareToMessage}
        onStartNewConversation={handleStartNewConversation}
      />
    </>
  );
};

export default GroupHeader;
